import { WalletConfig } from '@/features/swap/logic/types'
import { GSMBurnError, GsmBurnState } from './types'
import { DialogFormNormalizedData } from '@/features/dialogs/common/logic/form'
import { erc20Abi } from 'viem'
import { toBigInt } from '@/utils/bigNumber'
import { hyperEVM } from '@/config/chain/constants'
import { getContractAddress } from '@/domain/hooks/useContractAddress'
import { gsmConfig } from '@/config/contracts-generated'
import { waitForTxReceipt } from '@/utils/waitForTxReceipt'
import { captureError } from '../../../../instrumentation-client'
import { queryClient } from '@/config/query-client'
import { allowanceQueryKey } from '@/features/actions/flavours/approve/logic/query'
import { getBalancesQueryKeyPrefix } from '@/domain/wallet/getBalancesQueryKeyPrefix'

interface GsmBurnProps {
  setGsmBurnState: (state: GsmBurnState) => void
  fromAsset: DialogFormNormalizedData
  walletConfig: WalletConfig
  chainId: number
  onSuccess: () => void
}

export async function gsmBurn({ setGsmBurnState, fromAsset, walletConfig, chainId, onSuccess }: GsmBurnProps) {
  const { account, publicClient, walletClient } = walletConfig

  if (!account || !publicClient || !walletClient) {
    return
  }

  try {
    setGsmBurnState({ message: 'Initializing...', status: 'in-progress' })

    const gsmAddress = getContractAddress(gsmConfig.address, chainId)
    const gsmAbi = gsmConfig.abi

    const usdxlAmount = toBigInt(fromAsset.token.toBaseUnit(fromAsset.value))

    const [assetAmount] = await publicClient.readContract({
      address: gsmAddress,
      abi: gsmAbi,
      functionName: 'getAssetAmountForBuyAsset',
      args: [usdxlAmount],
    })

    if (assetAmount === 0n) {
      setGsmBurnState({ message: 'Amount too small to redeem', status: 'error' })
      return
    }

    const allowance = await publicClient.readContract({
      address: fromAsset.token.address,
      abi: erc20Abi,
      functionName: 'allowance',
      args: [account, gsmAddress],
    })

    if (allowance < usdxlAmount) {
      setGsmBurnState({ message: 'Approving...', status: 'in-progress' })

      const approveHash = await walletClient.writeContract({
        address: fromAsset.token.address,
        abi: erc20Abi,
        functionName: 'approve',
        args: [gsmAddress, usdxlAmount],
        chain: hyperEVM,
        account,
      })
      await waitForTxReceipt(publicClient, approveHash)
    }

    setGsmBurnState({ message: 'Burning...', status: 'in-progress' })

    const { request } = await publicClient.simulateContract({
      address: gsmAddress,
      abi: gsmAbi,
      functionName: 'buyAsset',
      args: [assetAmount, account],
      chain: hyperEVM,
      account,
    })

    const hash = await walletClient.writeContract(request)

    await waitForTxReceipt(publicClient, hash)

    void queryClient.invalidateQueries({
      queryKey: allowanceQueryKey({ token: fromAsset.token.address, spender: gsmAddress, account, chainId }),
    })

    void queryClient.invalidateQueries({
      queryKey: getBalancesQueryKeyPrefix({ chainId, account }),
    })

    setGsmBurnState({ message: '', status: 'idle' })
    onSuccess()
  } catch (error) {
    setGsmBurnState({ message: 'An error occurred while burning', status: 'error' })
    captureError(new GSMBurnError('GSM burn error'), { error })
  }
}
